import { useContext, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Add, Delete, Message, VpnKey } from '@mui/icons-material';
import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DropsContext } from 'contexts/DropsContext';
import { toastError } from 'utils/toast';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Header from 'components/Header';
import Input from 'components/Input';
import styles from './styles';

const types = [{ label: 'CRIAÇÃO', value: 'create' }];

const vars = [
  { label: 'Link da Ordem de Serviço', value: '#URLOS00000001' },
  { label: 'Nº do Documento', value: '#DOCUMENTO' },
];

const createMessage = (message = {}) => ({
  type: message?.type || null,
  message: message?.message || '',
  active: message?.active ?? false,
  status_id: message?.status_id || null,
  especie_id: message?.especie_id || null,
  natureza_operacao_id: message?.natureza_operacao_id || null,
});

const hasValue = (value) =>
  value !== null && value !== undefined && value !== '';

const getCaretCoordinates = (element, position) => {
  const div = document.createElement('div');
  const style = window.getComputedStyle(element);

  const properties = [
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontFamily',
    'lineHeight',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'whiteSpace',
    'wordBreak',
    'overflowWrap',
  ];

  properties.forEach((property) => {
    div.style[property] = style[property];
  });

  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.top = '0';
  div.style.left = '-9999px';

  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';

  div.textContent = element.value.substring(0, position);

  const span = document.createElement('span');

  span.textContent = element.value.substring(position) || '.';

  div.appendChild(span);

  document.body.appendChild(div);

  const coordinates = {
    top: span.offsetTop,
    left: span.offsetLeft,
  };

  document.body.removeChild(div);

  return coordinates;
};

const GptMaker = ({ logo, loading, integracao, handleSave }) => {
  const messageRefs = useRef({});
  const [tab, setTab] = useState(0);
  const [varMenu, setVarMenu] = useState({
    open: false,
    index: null,
    position: null,
    anchorPosition: null,
  });
  const access_data = integracao?.access_data;
  const defaultValues = {
    id: integracao?.id,
    // ACESSO
    nome_integracao: access_data?.nome_integracao || '',
    token: access_data?.token || '',
    channelId: access_data?.channelId || '',
    // MENSAGENS
    messages:
      access_data?.messages?.map((message) => createMessage(message)) || [],
  };
  const { drops } = useContext(DropsContext);
  const { control, handleSubmit, getValues, setValue } = useForm({
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'messages',
  });

  const handleAddMessage = () => {
    append(createMessage());
  };

  const handleValidateSave = (data) => {
    const invalidIndex = data?.messages?.findIndex((message) => {
      const hasType = hasValue(message?.type);
      const hasStatus = hasValue(message?.status_id);

      return hasType === hasStatus;
    });

    if (invalidIndex >= 0) {
      setTab(1);
      toastError({
        message: `No evento ${invalidIndex + 1}, selecione Tipo ou Status.`,
      });
      return;
    }

    handleSave(data);
  };

  const handleMessageChange = (e, index) => {
    const element = e.target;

    const value = element.value;
    const cursorPosition = element.selectionStart;

    if (!cursorPosition) return;

    const lastChar = value[cursorPosition - 1];

    if (lastChar !== '#') return;

    const caret = getCaretCoordinates(element, cursorPosition);

    const rect = element.getBoundingClientRect();

    const computedStyle = window.getComputedStyle(element);

    let lineHeight = parseFloat(computedStyle.lineHeight);

    if (Number.isNaN(lineHeight)) {
      lineHeight = parseFloat(computedStyle.fontSize || 16) * 1.2;
    }

    setVarMenu({
      open: true,
      index,
      position: cursorPosition - 1,

      anchorPosition: {
        top: rect.top + caret.top - element.scrollTop + lineHeight,

        left: rect.left + caret.left - element.scrollLeft,
      },
    });
  };

  const handleSelectVar = (variable) => {
    const { index, position } = varMenu;

    if (index === null || position === null) return;

    const fieldName = `messages.${index}.message`;

    const message = getValues(fieldName) || '';

    const newMessage =
      message.slice(0, position) + variable.value + message.slice(position + 1);

    const newCursorPosition = position + variable.value.length;

    setValue(fieldName, newMessage, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setVarMenu({
      open: false,
      index: null,
      position: null,
      anchorPosition: null,
    });

    requestAnimationFrame(() => {
      const input = messageRefs.current[index];

      if (!input) return;

      input.focus();

      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  const handleCloseVarMenu = () => {
    setVarMenu({
      open: false,
      index: null,
      position: null,
      anchorPosition: null,
    });
  };

  return (
    <Container>
      <Header titulo="Configurações" />
      <Grid container spacing={5} pb={12}>
        <Grid item xs={12}>
          <Box sx={styles?.header}>
            <Box
              src={logo}
              component="img"
              sx={styles?.img}
              alt={integracao?.api}
            />
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab
                value={0}
                icon={<VpnKey />}
                iconPosition="start"
                label="Acesso"
              />
              <Tab
                value={1}
                icon={<Message />}
                iconPosition="start"
                label="Mensagens"
              />
            </Tabs>
          </Box>
        </Grid>
        <Grid item xs={12} display={tab === 0 ? 'block' : 'none'}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Input
                control={control}
                name="nome_integracao"
                label="Nome da Integração"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input name="token" label="Token" control={control} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input name="channelId" label="ID do Canal" control={control} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} display={tab === 1 ? 'block' : 'none'}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="center"
              flexWrap="wrap"
              gap={1}
            >
              <Button
                color="primary"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddMessage}
              >
                Adicionar evento de mensagem
              </Button>
            </Grid>
            {fields.map((field, index) => (
              <Grid item xs={12} key={field.id}>
                <Box p={2} border={1} borderColor="divider" borderRadius={2}>
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Evento {index + 1}
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <Controller
                          name={`messages.${index}.active`}
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              label="Ativo"
                              control={
                                <Switch
                                  checked={Boolean(field.value)}
                                  onChange={(_, checked) =>
                                    field.onChange(checked)
                                  }
                                />
                              }
                            />
                          )}
                        />
                        <Button
                          color="error"
                          variant="outlined"
                          startIcon={<Delete />}
                          onClick={() => remove(index)}
                        >
                          Remover
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        name={`messages.${index}.type`}
                        control={control}
                        label="Tipo"
                        options={types}
                        onValueChange={(type) => {
                          if (!type) return;

                          setValue(`messages.${index}.status_id`, null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        name={`messages.${index}.status_id`}
                        control={control}
                        label="Status"
                        options={drops?.StatusDocumento}
                        onValueChange={(status) => {
                          if (!status) return;

                          setValue(`messages.${index}.type`, null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        name={`messages.${index}.especie_id`}
                        control={control}
                        label="Espécie"
                        options={drops?.EspecieDocumento}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        name={`messages.${index}.natureza_operacao_id`}
                        control={control}
                        label="Natureza da Operação"
                        options={drops?.NaturezaOperacao}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        name={`messages.${index}.message`}
                        placeholder="Digite a mensagem..."
                        control={control}
                        label="Mensagem"
                        multiline
                        rows={5}
                        inputRef={(element) => {
                          messageRefs.current[index] = element;
                        }}
                        onChange={(e) => handleMessageChange(e, index)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Para inserir variáveis na sua mensagem, digite <b>#</b>.
                      </Alert>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Box sx={styles?.bottom}>
        <Button
          color="primary"
          loading={loading}
          variant="contained"
          onClick={handleSubmit(handleValidateSave)}
        >
          Salvar
        </Button>
      </Box>
      <Popover
        open={varMenu.open}
        anchorReference="anchorPosition"
        anchorPosition={varMenu.anchorPosition}
        onClose={handleCloseVarMenu}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 250 } } }}
      >
        <List disablePadding>
          {vars.map((variable, index) => (
            <Box key={variable.value}>
              <ListItemButton onClick={() => handleSelectVar(variable)}>
                <ListItemText
                  primary={variable.label}
                  secondary={variable.value}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                />
              </ListItemButton>
              {index < vars.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      </Popover>
    </Container>
  );
};

export default GptMaker;
